import type { RouteObject } from "react-router-dom";
import { Navigate, Outlet } from "react-router-dom";
import { Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { ChatBubble } from "@/components/chat/ChatBubble";
import { usePageTracking } from "@/hooks/usePageTracking";

const queryClient = new QueryClient();

function PageTracker() {
  usePageTracking();
  return null;
}

function RootLayout() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <ScrollToTop />
            <PageTracker />
            <Outlet />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function PublicLayout() {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="flex-1">
        <Suspense fallback={<div className="min-h-screen" />}>
          <Outlet />
        </Suspense>
      </div>
      <Footer />
      <ChatBubble />
    </div>
  );
}

function AdminLayout() {
  return (
    <Suspense fallback={<div className="min-h-screen" />}>
      <Outlet />
    </Suspense>
  );
}

const lazyRoute = (importer: () => Promise<{ default: React.ComponentType }>) => async () => {
  const mod = await importer();
  return { Component: mod.default };
};

export const routes: RouteObject[] = [
  {
    path: "/",
    Component: RootLayout,
    children: [
      {
        path: "admin",
        Component: AdminLayout,
        children: [
          { index: true, lazy: lazyRoute(() => import("./pages/Admin")) },
        ],
      },
      {
        path: "",
        Component: PublicLayout,
        children: [
          { index: true, lazy: lazyRoute(() => import("./pages/Index")) },
          { path: "about", lazy: lazyRoute(() => import("./pages/About")) },
          { path: "contact", lazy: lazyRoute(() => import("./pages/Contact")) },
          { path: "projects", lazy: lazyRoute(() => import("./pages/Projects")) },
          { path: "projects/:slug", lazy: lazyRoute(() => import("./pages/ProjectDetail")) },
          { path: "blog", lazy: lazyRoute(() => import("./pages/Blog")) },
          { path: "blog/:slug", lazy: lazyRoute(() => import("./pages/BlogPost")) },
          { path: "service-areas", lazy: lazyRoute(() => import("./pages/ServiceAreas")) },
          { path: "service-areas/:slug", lazy: lazyRoute(() => import("./pages/service-areas/CityPage")) },
          { path: "gallery", lazy: lazyRoute(() => import("./pages/Gallery")) },
          { path: "careers", lazy: lazyRoute(() => import("./pages/Careers")) },

          { path: "services/lawn-care", lazy: lazyRoute(() => import("./pages/services/LawnCare")) },
          { path: "services/aeration", lazy: lazyRoute(() => import("./pages/services/Aeration")) },
          { path: "services/mulching", lazy: lazyRoute(() => import("./pages/services/Mulching")) },
          { path: "services/dethatching", lazy: lazyRoute(() => import("./pages/services/Dethatching")) },
          { path: "services/spring-fall-cleanups", lazy: lazyRoute(() => import("./pages/services/SpringFallCleanups")) },
          { path: "services/snow-removal", lazy: lazyRoute(() => import("./pages/services/SnowRemoval")) },

          { path: "about-us", element: <Navigate to="/about" replace /> },
          { path: "reviews", element: <Navigate to="/" replace /> },
          { path: "testimonials", element: <Navigate to="/" replace /> },

          { path: "services/fertilization", element: <Navigate to="/services/lawn-care" replace /> },
          { path: "services/hardscaping", element: <Navigate to="/services/mulching" replace /> },
          { path: "services/planting", element: <Navigate to="/services/mulching" replace /> },
          { path: "services/roof-replacement", element: <Navigate to="/services/lawn-care" replace /> },
          { path: "services/roof-repair", element: <Navigate to="/services/lawn-care" replace /> },
          { path: "services/siding-replacement", element: <Navigate to="/services/mulching" replace /> },
          { path: "services/siding-repair", element: <Navigate to="/services/mulching" replace /> },
          { path: "services/deck-installation", element: <Navigate to="/services/lawn-care" replace /> },
          { path: "services/gutter-installation", element: <Navigate to="/services/lawn-care" replace /> },
          { path: "services/gutter-repairs", element: <Navigate to="/services/lawn-care" replace /> },
          { path: "roofing", element: <Navigate to="/services/lawn-care" replace /> },
          { path: "siding", element: <Navigate to="/services/mulching" replace /> },
          { path: "gutters", element: <Navigate to="/services/lawn-care" replace /> },
          { path: "storm-damage", element: <Navigate to="/services/lawn-care" replace /> },
          { path: "ice-storms", element: <Navigate to="/services/snow-removal" replace /> },

          { path: "maple-grove-mn", element: <Navigate to="/service-areas/maple-grove" replace /> },
          { path: "plymouth-mn", element: <Navigate to="/service-areas/plymouth" replace /> },
          { path: "brooklyn-park-mn", element: <Navigate to="/service-areas/maple-grove" replace /> },

          { path: "*", lazy: lazyRoute(() => import("./pages/NotFound")) },
        ],
      },
    ],
  },
];

export { prerenderedRoutes } from "./prerendered-routes";
